import type { MeResponse, MembershipRole } from "@safeplate/contracts";

export interface OrganisationChoice {
  id: string;
  name: string;
  role: MembershipRole;
}

const roleRank: Record<MembershipRole, number> = {
  admin: 3,
  manager: 2,
  staff: 1,
};

export function organisationChoices(me: MeResponse): OrganisationChoice[] {
  const choices = new Map<string, OrganisationChoice>();

  for (const membership of me.memberships) {
    const current = choices.get(membership.org_id);
    if (!current || roleRank[membership.role] > roleRank[current.role]) {
      choices.set(membership.org_id, {
        id: membership.org_id,
        name: membership.org_name,
        role: membership.role,
      });
    }
  }

  return [...choices.values()].sort((left, right) =>
    left.name.localeCompare(right.name),
  );
}

export function resolveSelection<T extends { id: string }>(
  choices: readonly T[],
  preferredId: string | null,
): string | null {
  if (preferredId && choices.some((choice) => choice.id === preferredId)) {
    return preferredId;
  }
  return choices[0]?.id ?? null;
}

export function readStoredSelection(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function storeSelection(key: string, value: string | null): void {
  try {
    if (value) {
      globalThis.localStorage?.setItem(key, value);
    } else {
      globalThis.localStorage?.removeItem(key);
    }
  } catch {
    // Storage can be unavailable in private or embedded browser contexts.
  }
}

export const selectionKeys = {
  location: "safeplate.active-location",
  organisation: "safeplate.active-organisation",
} as const;
